import Phaser from 'phaser';

/**
 * Manages keyboard and mouse input.
 */
export class InputManager {
  private _scene: Phaser.Scene;
  private _keys = new Map<string, boolean>();
  private _keysPressed = new Map<string, boolean>();
  private _keysReleased = new Map<string, boolean>();
  private _mousePosition = { x: 0, y: 0 };
  private _mouseButtons = new Map<number, boolean>();
  private _mouseButtonsPressed = new Map<number, boolean>();
  private _mouseButtonsReleased = new Map<number, boolean>();

  constructor(scene: Phaser.Scene) {
    this._scene = scene;
    this._setupKeyboardListeners();
    this._setupMouseListeners();
  }

  /**
   * Call this at the start of each frame to update input state.
   */
  update(): void {
    // Clear pressed/released states
    this._keysPressed.clear();
    this._keysReleased.clear();
    this._mouseButtonsPressed.clear();
    this._mouseButtonsReleased.clear();

    // Update mouse position
    this._mousePosition.x = this._scene.input.x;
    this._mousePosition.y = this._scene.input.y;
  }

  /**
   * Check if a key is currently held down.
   */
  isKeyDown(key: string): boolean {
    return this._keys.get(key) ?? false;
  }

  /**
   * Check if a key was just pressed this frame.
   */
  isKeyPressed(key: string): boolean {
    return this._keysPressed.get(key) ?? false;
  }

  /**
   * Check if a key was just released this frame.
   */
  isKeyReleased(key: string): boolean {
    return this._keysReleased.get(key) ?? false;
  }

  /**
   * Get the current mouse position in game coordinates.
   */
  get mousePosition(): { x: number; y: number } {
    return { ...this._mousePosition };
  }

  /**
   * Check if a mouse button is currently held down.
   * 0 = left, 1 = middle, 2 = right
   */
  isMouseDown(button: number = 0): boolean {
    return this._mouseButtons.get(button) ?? false;
  }

  /**
   * Check if a mouse button was just pressed this frame.
   */
  isMousePressed(button: number = 0): boolean {
    return this._mouseButtonsPressed.get(button) ?? false;
  }

  /**
   * Check if a mouse button was just released this frame.
   */
  isMouseReleased(button: number = 0): boolean {
    return this._mouseButtonsReleased.get(button) ?? false;
  }

  /**
   * Clean up event listeners.
   */
  destroy(): void {
    this._scene.input.keyboard?.off('keydown');
    this._scene.input.keyboard?.off('keyup');
    this._scene.input.off('pointerdown');
    this._scene.input.off('pointerup');
  }

  private _setupKeyboardListeners(): void {
    this._scene.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (!this._keys.get(event.code)) {
        this._keysPressed.set(event.code, true);
      }
      this._keys.set(event.code, true);
    });

    this._scene.input.keyboard?.on('keyup', (event: KeyboardEvent) => {
      this._keys.set(event.code, false);
      this._keysReleased.set(event.code, true);
    });
  }

  private _setupMouseListeners(): void {
    this._scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      const button = pointer.button;
      if (!this._mouseButtons.get(button)) {
        this._mouseButtonsPressed.set(button, true);
      }
      this._mouseButtons.set(button, true);
    });

    this._scene.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      const button = pointer.button;
      this._mouseButtons.set(button, false);
      this._mouseButtonsReleased.set(button, true);
    });
  }
}

/**
 * Common key codes for convenience.
 */
export const Keys = {
  // Arrow keys
  ArrowUp: 'ArrowUp',
  ArrowDown: 'ArrowDown',
  ArrowLeft: 'ArrowLeft',
  ArrowRight: 'ArrowRight',

  // WASD
  W: 'KeyW',
  A: 'KeyA',
  S: 'KeyS',
  D: 'KeyD',

  // Common actions
  Space: 'Space',
  Enter: 'Enter',
  Escape: 'Escape',
  Shift: 'ShiftLeft',
  Control: 'ControlLeft',

  // Numbers
  Digit1: 'Digit1',
  Digit2: 'Digit2',
  Digit3: 'Digit3',
  Digit4: 'Digit4',
  Digit5: 'Digit5',
} as const;
